import { useLocalStorage } from '@cheqroom/hooks';
import { useFeature } from '@optimizely/react-sdk';
import { useEffect, useMemo, useRef } from 'react';

import useOverviewConfig, {
	getDefaultViewConfigWithInlineFields,
} from '../../components/Overview/use-overview-config.hook';
import { useAuth } from '../../context/Authentication.context';
import useItemTypes from '../item-types/use-item-types';
import { FieldType, SystemFieldType } from './types';

const CUSTOMIZE_ITEM_VIEW_DEFAULT_SELECTION = [SystemFieldType.barcode, SystemFieldType.qr, SystemFieldType.created];
const LEGACY_STORAGE_KEY_SUFFIX = 'app.item-view';

// Maps old field IDs to new SystemFieldType enum values
const LEGACY_FIELD_MAPPING: Record<string, FieldType> = {
	qr: SystemFieldType.qr, // 'qr' -> 'codes'
	barcode: SystemFieldType.barcode, // 'barcode' -> 'barcodes'
} as const;

type SaveItemFieldIds = (ids: FieldType[]) => void;

/**
 * Manages item field configuration for items overview.
 * Supports both legacy localStorage and new overview config storage via feature flag.
 *
 * When `items_overview_refactor` feature flag is enabled:
 * - Uses new overview config storage (namespaced by itemTypeId)
 * - Migrates data from legacy storage on first use
 * - Saves to new storage system
 *
 * When feature flag is disabled:
 * - Uses legacy localStorage storage
 * - Maps old field IDs to new enum values for backward compatibility
 *
 * @param itemTypeId - The item type ID to namespace the config
 * @returns Tuple of [selectedFieldIds, saveFunction]
 */
export const useItemFieldsConfig = (itemTypeId: string): [FieldType[], SaveItemFieldIds] => {
	const { id: userId } = useAuth();

	const { getDefaultItemType } = useItemTypes();
	const defaultItemType = getDefaultItemType();

	const [itemsConfig, setOverviewConfig] = useOverviewConfig<FieldType>({
		type: 'items-list',
		namespace: itemTypeId ?? defaultItemType,
		view: 'table',
		defaultConfig: getDefaultViewConfigWithInlineFields<FieldType>(
			'items-list',
			CUSTOMIZE_ITEM_VIEW_DEFAULT_SELECTION
		),
	});

	const storageKey = `${userId ?? ''}${LEGACY_STORAGE_KEY_SUFFIX}`;
	const [selectedFieldIds, saveSelectedFieldIds] = useLocalStorage<FieldType[]>(
		storageKey,
		CUSTOMIZE_ITEM_VIEW_DEFAULT_SELECTION
	);

	const [isNewItemsOverviewEnabled] = useFeature('items_overview_refactor');
	const migrationAttemptedRef = useRef(false);

	const currentInlineFields = useMemo(() => itemsConfig?.inlineFields ?? [], [itemsConfig?.inlineFields]);

	// Migrate from legacy storage to new storage system
	useEffect(() => {
		if (
			!isNewItemsOverviewEnabled ||
			migrationAttemptedRef.current ||
			!userId ||
			!storageKey ||
			!selectedFieldIds
		) {
			return;
		}

		const hasLegacyData = selectedFieldIds.length > 0;
		const hasNewData = currentInlineFields.length > 0;
		const shouldMigrate = !hasNewData && hasLegacyData;

		if (shouldMigrate) {
			migrationAttemptedRef.current = true;
			setOverviewConfig({
				...itemsConfig,
				inlineFields: selectedFieldIds,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isNewItemsOverviewEnabled, currentInlineFields.length, selectedFieldIds, userId, storageKey]);

	if (isNewItemsOverviewEnabled) {
		const inlineFields = itemsConfig?.inlineFields ?? [];

		// Create a save function that writes to the new storage system
		const saveToNewStorage: SaveItemFieldIds = (ids: FieldType[]) => {
			setOverviewConfig({
				...itemsConfig,
				inlineFields: ids,
			});
		};

		return [inlineFields, saveToNewStorage];
	}

	const mappedFieldIds = (selectedFieldIds ?? []).map((id) => {
		return LEGACY_FIELD_MAPPING[id] ?? id;
	});

	return [mappedFieldIds, saveSelectedFieldIds];
};

export default useItemFieldsConfig;
